import { PaginationApiType } from '@shared/types/data-table.types';
import {
  EdgeAPI,
  NodeAPI,
  type NodeMeta,
} from '@app/pages/audience-manager/pages/builder/shared/models/builder-data.model';

export enum FrequencyValues {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export enum ExportType {
  IMMEDIATE = 'immediate',
  SCHEDULED = 'scheduled',
}

export enum ScheduledType {
  ONE_TIME = 'oneTime',
  RECURRING = 'recurring',
}

export enum ExportStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
  RUNNING = 'running',
  SCHEDULED = 'scheduled',
  CANCELLED = 'cancelled',
}

export interface Export {
  id: string;
  exportName: string;
  audienceName: string;
  exportType: ExportType;
  status: ExportStatus;
  scheduledType: ScheduledType | null;
  startedDate: Date | null;
  lastRun: Date | null;
  currentRuns: number;
  records: number;
  initiatedBy: string;
}

export interface GetExportsApiResponse {
  exports: Export[];
  pagination: PaginationApiType;
}
export interface ScheduledConfig {
  scheduleType: ScheduledType;
  startDate: string;
  endDate: string | null;
  time: string;
  frequency: FrequencyValues | null;
}

export enum DestinationDisabledReason {
  COMING_SOON = 'COMING_SOON',
  ACTIVE_EXPORT = 'ACTIVE_EXPORT',
  TENANT_NOT_AVAILABLE = 'TENANT_NOT_AVAILABLE',
}

export enum ExportDestinationKey {
  GOOGLE_BIGQUERY = 'googleBigQuery',
  DSP = 'DSP',
  AMAZON_S3 = 'amazonS3',
  GOOGLE_CLOUD_STORAGE = 'googleCloudStorage',
}

export type ExportWritePreference = 'append' | 'truncate';

/** Destination option rendered in the destination picker grid */
export interface DestinationOption {
  id: string;
  key?: ExportDestinationKey;
  name: string;
  description: string;
  selected?: boolean;
  isDisabled: boolean;
  /** When true, the destination is unavailable for the current tenant. */
  isTenantDisabled?: boolean;
  disabledReason?: DestinationDisabledReason;
}

export interface GoogleBigQueryDestination {
  id: string;
  dataset: string;
  table: string;
  writePreference: ExportWritePreference;
}

export interface DspDestination {
  id: string;
  segmentName: string;
  ttl: number | null;
}

export interface ExportDestinationsConfig {
  googleBigQuery: GoogleBigQueryDestination | null;
  DSP: DspDestination | null;
  amazonS3: null;
  googleCloudStorage: null;
}

export interface ExportField {
  id: string;
  label: string;
  required?: boolean;
  description?: string;
  type?: string[];
  categoryId?: number;
  sourceLabel?: string | undefined;
}

export interface CreateExport {
  audienceId: string;
  audienceName: string | null;
  fields: ExportField[];
  exportType: ExportType;
  scheduledConfig: ScheduledConfig | null;
  exportName: string;
  selectedDestinationKey: ExportDestinationKey | null;
  destination: ExportDestinationsConfig;
  nodes?: NodeAPI[];
  edges?: EdgeAPI[];
}

export interface CreateExportApiPayload {
  audienceId: string;
  audienceName: string | null;
  fields: { id: string; sourceLabel: string }[];
  exportType: ExportType;
  scheduledConfig: ScheduledConfig | null;
  exportName: string;
  destination: ExportDestinationsConfig;
}

export interface StepperConfig {
  label: string;
  value: number;
}

export interface exportField {
  // This export field id is the attribute in a different structure
  id: string;
  label: string;
  categoryId: number;
  required: boolean;
  sourceLabel: string | undefined;
}

export interface exportCategory {
  id: number;
  label: string;
}

export interface GetFieldsApiResponse {
  categories: exportCategory[];
  fields: exportField[];
}

export interface SearchedFieldItem {
  id: string;
  label: string;
  meta: string;
}

export interface SearchedFieldItemApiResponse {
  id: string;
  name: string;
  meta: string;
}

export interface SearchedFieldsApiResponse {
  label: string;
  value: string;
  items: SearchedFieldItemApiResponse[];
}

export interface SearchedFields {
  label: string;
  value: string;
  items: SearchedFieldItem[];
}

/**
 * Builder search item shape that supports both attributes and custom audiences.
 */
export interface BuilderSearchedFieldItem extends Omit<
  SearchedFieldItem,
  'meta'
> {
  name?: string;
  meta?: string | NodeMeta;
  nodeType?: 'audience';
  size?: number | string;
  description?: string;
  sizeUpdatedAt?: string;
}

/**
 * Grouped builder search results shape.
 */
export interface BuilderSearchedFields {
  label: string;
  value: string;
  items: BuilderSearchedFieldItem[];
}

export interface DatasetsGetResponse {
  datasets: string[];
}

export interface TableListGetResponse {
  tables: string[];
}
